/**
 * BranchSwitcher — header dropdown to pick the active branch.
 * Displays the current branch (with its color + icon) and lets the user
 * switch to any active branch or fall back to "All branches".
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  Store, Building2, Warehouse, Sparkles, Users,
  ShoppingBag, ShoppingCart, MapPin, Layers, ChevronDown, Check, Globe,
} from 'lucide-react';
import { useBranch } from '../../context/BranchContext';

const ICON_MAP = {
  Store, Building2, Warehouse, Sparkles, Users,
  ShoppingBag, ShoppingCart, MapPin, Layers,
};

export default function BranchSwitcher() {
  const { allBranches, currentBranchId, currentBranch, setBranch } = useBranch();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  if (!allBranches || allBranches.length === 0) return null;

  const activeBranches = allBranches.filter(
    (b) => b.status === 'active' && !b.is_deleted
  );
  const Icon = currentBranch
    ? (ICON_MAP[currentBranch.icon] || Store)
    : Globe;
  const tone = currentBranch?.color || '#64748b';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1.5 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="branch-switcher-button"
      >
        <span
          className="flex h-6 w-6 items-center justify-center rounded text-white"
          style={{ backgroundColor: tone }}
        >
          <Icon size={13} />
        </span>
        <span className="font-medium text-foreground truncate max-w-[140px]">
          {currentBranch ? currentBranch.name_ar : 'كل الفروع'}
        </span>
        <ChevronDown size={14} className="text-muted-foreground" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute end-0 mt-2 w-72 rounded-xl border border-border bg-card shadow-lg z-50 overflow-hidden"
          data-testid="branch-switcher-menu"
        >
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/30">
            اختر الفرع النشط
          </div>
          <ul className="max-h-72 overflow-y-auto">
            <li>
              <button
                type="button"
                onClick={() => { setBranch(''); setOpen(false); }}
                className="flex w-full items-center gap-3 px-3 py-2 text-sm hover:bg-muted transition-colors text-start"
                role="option"
                aria-selected={!currentBranchId}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded text-white bg-neutral-500">
                  <Globe size={14} />
                </span>
                <span className="flex-1 text-foreground">كل الفروع</span>
                {!currentBranchId && <Check size={14} className="text-primary-600" />}
              </button>
            </li>
            {activeBranches.map((b) => {
              const I = ICON_MAP[b.icon] || Store;
              const selected = b.id === currentBranchId;
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => { setBranch(b.id); setOpen(false); }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-sm hover:bg-muted transition-colors text-start"
                    role="option"
                    aria-selected={selected}
                  >
                    <span
                      className="flex h-7 w-7 items-center justify-center rounded text-white"
                      style={{ backgroundColor: b.color || '#3b82f6' }}
                    >
                      <I size={14} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-foreground truncate">{b.name_ar}</span>
                      <span className="block text-xs text-muted-foreground font-mono">
                        {b.branch_code}
                      </span>
                    </span>
                    {b.is_main && (
                      <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                        رئيسي
                      </span>
                    )}
                    {selected && <Check size={14} className="text-primary-600" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
